import { Menu, Transition, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import clsx from 'clsx';
import { Fragment, type MutableRefObject, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

import Typography from '~/shared/components/Typography';

const transitionProps = {
  enter: 'transition ease-out duration-100',
  enterFrom: 'transform opacity-0 scale-95',
  enterTo: 'transform opacity-100 scale-100',
  leave: 'transition ease-in duration-75',
  leaveFrom: 'transform opacity-100 scale-100',
  leaveTo: 'transform opacity-0 scale-95',
};

interface SimpleDropdownProps<T extends string = string> {
  items: T[];
  selectedItem: T;
  onSelect: (item: T) => void;
  align?: 'top-right' | 'top-left';
  className?: string;
  menuButtonClassName?: string;
  menuItemClassName?: string;
  menuItemsClassName?: string;
  disabled?: boolean;
  'aria-label'?: string;
  displayLabel?: string;
  isItemSelected?: (item: T) => boolean;
  renderItemEnd?: (item: T) => ReactNode;
  renderButtonEnd?: () => ReactNode;
  fullWidth?: boolean;
  portal?: boolean;
  menuButtonRef?: MutableRefObject<HTMLButtonElement | null>;
  onMenuButtonClick?: () => void;
  menuButtonLabelClassName?: string;
}

export default function SimpleDropdown<T extends string = string>({
  items,
  selectedItem,
  onSelect,
  align = 'top-right',
  className,
  menuButtonClassName,
  menuItemClassName,
  menuItemsClassName,
  disabled = false,
  'aria-label': ariaLabel,
  displayLabel,
  isItemSelected,
  renderItemEnd,
  renderButtonEnd,
  fullWidth = false,
  portal = false,
  menuButtonRef,
  onMenuButtonClick,
  menuButtonLabelClassName,
}: SimpleDropdownProps<T>) {
  const checkSelected = isItemSelected ?? ((item: T) => item === selectedItem);
  const buttonLabel = displayLabel ?? selectedItem;

  const menuItems = (
    <Transition as={Fragment} {...transitionProps}>
      <MenuItems
        anchor={portal ? { to: 'bottom start' } : undefined}
        className={clsx(
          portal
            ? 'rounded-lg bg-white z-[99999]'
            : 'absolute top-[100%] rounded-lg mt-2 origin-top-right bg-white z-30',
          !portal && (fullWidth ? 'w-full' : 'w-full'),
          'shadow-filter-dropdown focus-outline-none border-primary-70',
          'border border-primary-70 focus:outline-hidden',
          align === 'top-right' ? 'right-0 origin-top-right' : 'left-0 origin-top-left',
          menuItemsClassName
        )}
      >
        <div className="p3">
          {items.map((item) => {
            const selected = checkSelected(item);

            return (
              <MenuItem
                as={'button'}
                key={item}
                className={clsx(
                  'text-sm leading-6 w-full h-9! h-11! flex items-center justify-between px-5 py-[18px] border-gray-200 last:rounded-b-lg cursor-pointer hover:bg-primary-20 group',
                  items[0] === item && 'rounded-t-lg',
                  selected && 'bg-primary-20 border border-primary-500',
                  !selected && item !== items[items.length - 1] && 'border-b border-gray-200',
                  menuItemClassName
                )}
                onClick={() => {
                  onSelect?.(item);
                }}
              >
                <Typography color="text-primary-900" variant="p3-reg">
                  {item}
                </Typography>
                {renderItemEnd?.(item)}
              </MenuItem>
            );
          })}
        </div>
      </MenuItems>
    </Transition>
  );

  return (
    <div className={clsx('relative', fullWidth && 'w-full', className)}>
      <Menu as="div" className={clsx('relative flex', fullWidth && 'w-full')}>
        {({ open }) => (
          <>
            <MenuButton
              ref={(el) => {
                if (menuButtonRef) {
                  menuButtonRef.current = el;
                }
              }}
              disabled={disabled}
              aria-label={ariaLabel}
              className={clsx(
                'bg-white text-primary-900 border border-gray-300 flex justify-between rounded-[8px] px-3 h-9 h-11 text-[14px] w-full',
                disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                !disabled && open
                  ? 'bg-primary-20 border-primary-200'
                  : !disabled && 'hover:border-primary-500 active:border-primary-100',
                'focus:border-primary-300 focus:outline-none',
                menuButtonClassName
              )}
              onClick={onMenuButtonClick}
            >
              <div className="flex items-center justify-between w-full">
                <Typography variant="p3-reg" color="text-gray-700" className={menuButtonLabelClassName}>
                  {buttonLabel}
                </Typography>
                <div className="flex items-center gap-2">
                  {renderButtonEnd?.()}
                  <ChevronDownIcon
                    className={clsx(
                      'w-[14px] h-[14px] text-primary-900 stroke-3 cursor-pointer',
                      open ? 'rotate-180' : ''
                    )}
                  />
                </div>
              </div>
            </MenuButton>

            {portal && typeof document !== 'undefined'
              ? createPortal(menuItems, document.body)
              : menuItems}
          </>
        )}
      </Menu>
    </div>
  );
}
